import { useDragScroll } from './useDragScroll';
import { Loader2 } from 'lucide-react';
import { useSubmodels } from './listingStore';
import { ListingGallery } from './ListingGallery';
import { useSubmodelOptions } from './SubmodelPicker';
import { materialText } from './variantLabel';
import { alignMaterials } from './alignMaterials';
import { PANEL_CARD, PanelTitle } from './PanelCard';
import { useLanguage } from '../../contexts/LanguageContext';

// WHAT EACH LISTING SAYS: ONE ROW PER SUBMODEL with a link — its name, what it is made of,
// and its pictures. A row is a submodel, and a column is a question. The links themselves
// are typed in the Find the links box above (ListingLinksTable), where links are found.
//
// The manual is the required source; a listing is a second, independent one — one link per
// colour/material "submodel" of the same model (see listingStore).
export function ListingsTable({ docId }: { docId: string }) {
  const { t } = useLanguage();
  const submodels = useSubmodels(docId);
  // NAMED AS THE FILTER NAMES IT, from useSubmodelOptions — the label the sidebar's picker
  // shows, falling back to a number for a link with nothing read yet. A second naming scheme
  // would leave two names on screen for one thing and no way to tell they are the same.
  const options = useSubmodelOptions(docId);
  // THE COMMON SHAPE: every submodel gets the number of materials most of them name, so a
  // wood sits on the same line of its cell as every other submodel's wood, and an empty line
  // says a listing named fewer than the majority. See alignMaterials — a majority vote, it
  // costs nothing, and it cannot fail.
  const aligned = alignMaterials(submodels);
  // THE WHOLE TABLE SLIDES, dragged anywhere on it: a row is one submodel, and a strip of
  // pictures scrolling inside its own cell left the link and the materials behind.
  const scrollRef = useDragScroll<HTMLDivElement>();

  const cell = 'border-r-2 border-slate-300 px-3 py-2 align-middle last:border-r-0';
  const head = `${cell} font-semibold text-slate-900`;

  return (
    <div className={`${PANEL_CARD} flex flex-col items-center gap-3 px-4 pb-5 pt-3`}>
      <PanelTitle>{t('notice.listing.section')}</PanelTitle>

      {/* No link yet, no table: every row would be empty. */}
      {submodels.some((s) => s.url) && (
      <div
        ref={scrollRef}
        className="w-full cursor-grab overflow-x-auto rounded-xl border-2 border-slate-300 bg-white shadow-sm"
      >
        {/* As wide as its pictures: the last column is every picture of the row, side by
            side, and the table is what scrolls. */}
        <table className="min-w-full text-center text-sm">
          <colgroup>
            <col style={{ width: '9rem', minWidth: '9rem' }} />
            <col style={{ width: '11rem', minWidth: '11rem' }} />
            <col />
          </colgroup>
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className={head}>{t('notice.info.submodel')}</th>
              <th className={head}>{t('notice.info.materialColour')}</th>
              <th className={head}>{t('notice.listing.gallery')}</th>
            </tr>
          </thead>
          <tbody>
            {submodels.map((s, i) => (s.url ? (() => {
              // Queued for this run OR reading now: either way the name and the materials
              // wait, rather than leave the previous run's on screen until its call comes
              // round. The gallery says the same thing its own way (see ListingGallery).
              const busy = s.detecting || s.pending;
              const pairs = aligned[i] ?? [];
              return (
                <tr key={s.id} className="border-t-2 border-slate-300 first:border-t-0">
                  {/* THE NAME IS ALSO AN ANSWER FROM THIS READ: a submodel is named by what
                      it is made of, and that is precisely what the read replaces. The number
                      stays through it — it is the link's position, not anything read off the
                      page, and it is what tells you WHICH submodel is being read. */}
                  <td className={`${cell} min-w-[9rem] font-semibold text-slate-900`}>
                    {busy ? (
                      <span className="inline-flex items-center gap-1.5">
                        {`${i + 1} - `}
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      </span>
                    ) : (
                      options[i]?.label
                    )}
                  </td>
                  {/* ONE LINE PER PAIR, because a material and its finish are one answer.
                      Through materialText, the formatter the parts table and the mesh's
                      material menu use — the mesh menu matches a part's material by its
                      printed text, so a second copy of this rule would stop it matching. */}
                  <td className={`${cell} min-w-[11rem] font-semibold text-slate-900`}>
                    {busy ? (
                      <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                    ) : pairs.length > 0 && (
                      <div className="flex flex-col divide-y divide-slate-200">
                        {pairs.map((v, j) => (
                          <span key={j} className="py-1">
                            {/* A blank line, not a dash, but a line all the same: it keeps the
                                next material level with the other rows'. */}
                            {(v && materialText(v)) || '\u00a0'}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className={cell}>
                    <ListingGallery docId={docId} submodelId={s.id} />
                  </td>
                </tr>
              );
            })() : null))}
          </tbody>
        </table>
      </div>
      )}

    </div>
  );
}
